/**
 * Panneau « Traiter » : les traitements de l'ancien éditeur, appliqués
 * directement au clip sélectionné dans la timeline.
 * Analyse (tempo/tonalité) · Quantification warp · Nettoyage (débruitage).
 */
import { useState } from 'react';
import * as api from '../api/client';
import { decode } from '../audio/context';
import { toMono, encodeWav } from '../audio/wav';
import type { Clip, StudioAction } from '../state/studio';
import type { Analysis } from '../types';

interface Props {
  clip: Clip;
  projectBpm: number;
  dispatch: React.Dispatch<StudioAction>;
  onClose: () => void;
  onError: (msg: string) => void;
}

/**
 * Extrait la tranche du clip en WAV — même chemin d'encodage que l'éditeur
 * (éprouvé en prod). `maxDur` permet de plafonner ce qu'on envoie au serveur
 * (l'analyse n'a pas besoin de plus de ~90 s, et un morceau entier peut
 * mettre le conteneur à genoux).
 */
function clipToWav(clip: Clip, maxDur?: number): Blob {
  const dur = maxDur ? Math.min(clip.duration, maxDur) : clip.duration;
  return encodeWav(
    toMono(clip.buffer, clip.offset, clip.offset + dur),
    clip.buffer.sampleRate,
  );
}

export default function ClipTools({ clip, projectBpm, dispatch, onClose, onError }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [info, setInfo] = useState<Analysis | null>(null);
  const [strength, setStrength] = useState(0.9);
  const [amount, setAmount] = useState(0.6);

  const apply = (buffer: AudioBuffer) => {
    dispatch({ type: 'clip/replace', id: clip.id, buffer, offset: 0, duration: buffer.duration });
  };

  const analyze = async () => {
    setBusy('Analyse du clip…');
    try {
      setInfo(await api.analyze(clipToWav(clip, 90)));
    } catch (e) {
      onError(e instanceof api.ApiError ? e.message : 'Le serveur d’analyse ne répond pas.');
    } finally {
      setBusy(null);
    }
  };

  const quantize = async () => {
    setBusy('Calage au tempo…');
    try {
      const { wav } = await api.quantize(clipToWav(clip), {
        bpm: projectBpm, division: 4, strength, mode: 'warp',
      });
      apply(await decode(wav));
    } catch (e) {
      onError(e instanceof api.ApiError ? e.message : 'Le serveur de calage ne répond pas.');
    } finally {
      setBusy(null);
    }
  };

  const denoise = async () => {
    setBusy('Nettoyage du clip…');
    try {
      const { wav } = await api.denoise(clipToWav(clip), {
        amount, useHighpass: true, useHum: true,
      });
      apply(await decode(wav));
    } catch (e) {
      onError(e instanceof api.ApiError ? e.message : 'Le serveur de nettoyage ne répond pas.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="cliptools" role="dialog" aria-label={`Traiter le clip ${clip.name}`}>
      <div className="ct-head">
        <strong>✨ Traiter</strong>
        <span className="ct-name">{clip.name}</span>
        <button className="btn xs ghost" onClick={onClose} aria-label="Fermer">✕</button>
      </div>

      <div className="ct-row">
        <button className="btn block" onClick={analyze} disabled={!!busy}>🔍 Analyser (tempo · tonalité)</button>
        {info && (
          <div className="ct-info">
            {info.bpm ? <span>{info.bpm.toFixed(1).replace('.', ',')} BPM</span> : <span>tempo non détecté</span>}
            {info.key ? <span> · {info.key.name}</span> : null}
            {info.bpm ? (
              <button
                className="btn xs"
                onClick={() => dispatch({ type: 'bpm', bpm: Math.round(info.bpm!) })}
                title="Utiliser ce tempo pour tout le projet"
              >→ tempo du projet</button>
            ) : null}
          </div>
        )}
      </div>

      <div className="ct-row">
        <button className="btn block primary" onClick={quantize} disabled={!!busy}>
          🎯 Caler au tempo du projet ({Math.round(projectBpm)} BPM)
        </button>
        <label className="ct-slider">
          Force
          <input
            type="range" min={10} max={100} value={Math.round(strength * 100)}
            onChange={(e) => setStrength(Number(e.target.value) / 100)}
          />
          <span className="mono">{Math.round(strength * 100)} %</span>
        </label>
      </div>

      <div className="ct-row">
        <button className="btn block" onClick={denoise} disabled={!!busy}>🧹 Nettoyer (souffle · ronflette)</button>
        <label className="ct-slider">
          Intensité
          <input
            type="range" min={10} max={100} value={Math.round(amount * 100)}
            onChange={(e) => setAmount(Number(e.target.value) / 100)}
          />
          <span className="mono">{Math.round(amount * 100)} %</span>
        </label>
      </div>

      <p className="note">Le résultat remplace le clip, à sa place dans la timeline.</p>
      {busy && <div className="ct-busy">{busy}</div>}
    </div>
  );
}
